/*#
# Skybox Baker

**Stand inside a galaxy and photograph the sky.** Six 90° renders from one point
become a cube map — a starfield with real structure, for the cost of a texture
fetch at runtime instead of tens of thousands of particles every frame.

The point is not that it is cheaper, though it is. It is that **the sky you bake
is the galaxy you can fly to**: you are photographing actual star positions from
an actual system, so the constellations a character sees from the ground are the
same objects on the navigation chart. A decorative starfield can never be that,
however pretty.

## Demo

```js
import { b3d, b3dGalaxy, bakeSkyboxCube, bakeSkyPair, starsFromVoxelGalaxy, facesToZip, defaultBakePose, SHIPPED_SKY, button3d, label3d, slider3d } from 'tosijs-3d'
import { tosi } from 'tosijs'

const { bake } = tosi({
  bake: { seed: 1234, stars: 10000, particleSize: 0.7, outFraction: 0.55, offPlane: 1, roll: 0, face: 1024, status: 'ready' },
})

let sceneEl = null

// THE GALAXY DOES NOT REBUILD ITSELF. Changing `starCount` sets a property and
// nothing more — `regenerate()` is what rebuilds the particle systems, so an
// observer below calls it. Without that every slider here would be inert.
//
// Debounced: a drag would otherwise queue one rebuild per step.
const galaxy = b3dGalaxy({
  seed: bake.seed,
  starCount: bake.stars,
  // The shipped sky's dim budget: dim stars only exist near the bake point,
  // so this costs nothing until the pair bake gathers them.
  dimBudget: SHIPPED_SKY.voxel.dimBudget,
  particleSize: bake.particleSize,
  coreSize: 0.12,
  radius: 100,
})

let pending = 0
for (const key of ['seed', 'stars', 'particleSize']) {
  bake[key].observe(() => {
    clearTimeout(pending)
    pending = setTimeout(() => galaxy.regenerate(), 400)
  })
}

preview.append(
  b3d(
    {
      style: 'width:100%;height:100%',
      clearColor: '#01020a',
      panelTitle: 'Bake a sky',
      scenePanel: () => [
        // 10k to work with, 100k to finish with — see "Framing, then baking".
        slider3d({ label: 'stars', value: bake.stars, min: 5000, max: 100000, step: 5000 }),
        slider3d({ label: 'particle size', value: bake.particleSize, min: 0.1, max: 1.5, step: 0.05 }),
        slider3d({ label: 'out from core', value: bake.outFraction, min: 0.1, max: 0.9, step: 0.05 }),
        slider3d({ label: 'off plane', value: bake.offPlane, min: 0, max: 6, step: 0.25 }),
        slider3d({ label: 'roll', value: bake.roll, min: -180, max: 180, step: 5 }),
        slider3d({ label: 'face px', value: bake.face, min: 256, max: 4096, step: 256 }),
        label3d({ text: bake.status, muted: true }),
        button3d({ label: 'bake cube', handleClick: async () => {
          bake.status = 'baking…'
          const faces = await bakeSkyboxCube(sceneEl.scene, {
            ...defaultBakePose(100, bake.outFraction.valueOf(), bake.offPlane.valueOf()),
            size: bake.face.valueOf(),
            roll: bake.roll.valueOf(),
          })
          // ONE zip, not six downloads — a burst of automatic downloads gets
          // throttled and dropped by the browser (see facesToZip).
          const zip = facesToZip([{ prefix: 'sky', faces }], 'sky-cube.zip')
          const a = document.createElement('a')
          a.href = URL.createObjectURL(zip.blob)
          a.download = zip.name
          a.click()
          setTimeout(() => URL.revokeObjectURL(a.href), 5000)
          bake.status = `saved ${zip.name} (${faces.length} faces)`
        } }),
        // THE PAIR: a 256 smooth cube for the nebulae plus a 1024 DATA cube
        // the sky shader decodes into points. The right output once the star
        // slider is at 100k — a plain raster at that density is a picture of
        // itself, and it would need 4096 to stop smearing.
        // THE SHIPPED SKY'S RECIPE — sets every slider to what produced
        // static/sky, so a rebake is one tap here and one tap below. (It used
        // to live only in commit messages; reproducing it took archaeology.)
        button3d({ label: 'use shipped recipe', handleClick: () => {
          bake.stars = SHIPPED_SKY.voxel.brightBudget
          bake.particleSize = SHIPPED_SKY.particleSize
          bake.outFraction = SHIPPED_SKY.outFraction
          bake.offPlane = SHIPPED_SKY.offPlane
          bake.roll = SHIPPED_SKY.roll
          bake.status = 'shipped recipe — rebuilding, then bake pair'
        } }),
        button3d({ label: 'bake pair (256 + 1024 data)', handleClick: async () => {
          bake.status = 'baking pair…'
          const eye = defaultBakePose(100, bake.outFraction.valueOf(), bake.offPlane.valueOf())
          // THE POINTS come from the voxel galaxy: every bright star, the dim
          // stars around the eye, the shell — what bin/bake-stars writes too.
          const sky = starsFromVoxelGalaxy(galaxy.galaxy, undefined, eye, {
            radius: 100,
            dimReach: SHIPPED_SKY.voxel.dimReach,
            floor: SHIPPED_SKY.voxel.floor,
          })
          const res = await bakeSkyPair(sceneEl.scene, galaxy, {
            ...eye,
            roll: bake.roll.valueOf(),
            smoothSize: 256,
            dataSize: 1024,
            objects: sky.objects,
          })
          const zip = facesToZip(
            [
              { prefix: 'nebula', faces: res.smooth },
              { prefix: 'stars', faces: res.data },
            ],
            'sky-pair.zip'
          )
          const a = document.createElement('a')
          a.href = URL.createObjectURL(zip.blob)
          a.download = zip.name
          a.click()
          setTimeout(() => URL.revokeObjectURL(a.href), 5000)
          bake.status = `pair baked: ${res.placed} placed, ${res.lost} lost`
        } }),
      ],
      sceneCreated(el) { sceneEl = el },
    },
    galaxy
  )
)
```
```css
.preview { height: 100%; }
```

## Six cameras, and why not a probe

`ReflectionProbe` is the better idea on paper — it renders a correctly-oriented
cube, so the face conventions stay Babylon's problem rather than ours — but its
`cubeTexture.readPixels(face)` returns all zeros here, every face. Six silently
black squares is a worse failure than a little more code, so the faces are
rendered with a camera instead.

The orientation table is therefore written out in full rather than trusted to
memory. The ±Y rows are the ones that bite: they need a different UP vector,
because "up" is undefined when you are looking straight up, and getting it wrong
gives you a sky with one face rotated 90° that nobody notices until a player
stares at the zenith.

## Where to stand

`defaultBakePose` puts you **55% of the way out from the core and slightly above
the plane**, which is roughly where Earth is — the Sun sits about 8 of the disc's
~13 kpc out, a few tens of parsecs up. Each part earns its place:

| choice | what it buys |
| ------ | ------------ |
| 50–60% out | the core is off to one side and bright, the far rim thin behind it. Dead centre is dense in every direction; the rim leaves one half empty |
| slightly off-plane | lets the band read AS a band — and TINY is the word. The disc half-thickness here is ~12, so 1 is plenty; 3 already reads as looking down on the galaxy rather than living in it |
| **tilted** | the band ARCS across the sky instead of lying level — and a level band reads as wallpaper the moment anyone looks up. Applied to the CUBE, not the galaxy: rotating the subject moves it out from under the camera |

The tilt is the one worth insisting on, and it is free: it is a property of the
capture basis, not the galaxy, so it costs one rotation and no geometry. Seed it
per system and no two stars share a sky.

## Framing, then baking

The star count defaults to **10,000 and goes to 100,000**, and the gap is
deliberate: you want the scene responsive while you are deciding where to stand
and how big the stars should be, and dense only for the shot you keep.

Generation is cheap: planets are computed on demand, names come from each
star's own seed, and each star uses a cheap PRNG. Measured, 10k bright stars
(with their interesting dim ones) build in about 0.4 s and 100k in about 2 s,
so the slider is debounced rather than a separate "now bake it" step.

⚠️ **Tune particle size AT 90°.** A cube face is 90° FOV where a typical demo
camera is nearer 46°, so a star covers about half the angular fraction of the
frame once you widen it. Anything dialled in while orbiting the galaxy comes out
small in the bake — 0.7 here against 0.3–0.5 there.
*/
/*{ "parent": "demos", "order": 40 }*/

import * as BABYLON from '@babylonjs/core'
import { zipSync } from 'fflate'
import {
  FACE_NAMES,
  encodeStarfield,
  spectralValue,
  type SkyObject,
} from './starfield-codec.js'
import { pngEncode } from './png.js'
import { CheapPRNG } from './mersenne-twister.js'
import {
  starDetailFor,
  type DistantStarData,
  type NebulaData,
} from './galaxy-data.js'
import type { MixEntry, VoxelGalaxy, VoxelStar } from './voxel-galaxy.js'

/** A point the baker reads — what `b3d-galaxy`'s point accessors return. */
export interface SkyPoint {
  position: { x: number; y: number; z: number }
  scaling?: { x: number }
  color?: { r: number; g: number; b: number } | null
}

/** One baked cube face: a PNG data URL plus the suffix Babylon expects. */
export interface BakedFace {
  /** `px` | `nx` | `py` | `ny` | `pz` | `nz` — the `CubeTexture` file suffix. */
  name: string
  /** `data:image/png;base64,…` */
  url: string
}

export interface SkyboxBakeOptions {
  /** Where the camera stands, in world space. */
  x: number
  y: number
  z: number
  /**
   * Pixels per cube face. Each face is a genuine render at this size, not an
   * upscale, so it is the only knob that buys actual sky detail.
   *
   * A face covers 90°, so it is stretched across a large part of a wide
   * viewport — 512 reads as soft the moment the sky fills the screen.
   *
   * ⚠️ **The shipped sky is no longer one of these.** `reference/sky/default`
   * is a 2048 raster kept for comparison (out of `static/`, so nothing serves
   * it), but what the demos load is the PAIR that {@link bakeSkyPair}
   * produces: a 256 smooth cube for the nebulae and a 1024 DATA cube for the
   * points. About a quarter of the raster's disk and 25 MiB of VRAM against
   * its 96 (the disk figure moves with every rebake, so only the ratio is
   * written down) — and the stars stay points at any zoom instead of being a
   * smear baked at one resolution.
   *
   * Reach for a big raster only when what you are baking is genuinely
   * low-frequency everywhere. The moment it contains points, encode the points.
   *
   * | face | VRAM (RGBA, 6 faces) |
   * | ---- | -------------------- |
   * | 512 | 6 MiB |
   * | 1024 | 24 MiB |
   * | 2048 | 96 MiB |
   * | 4096 | 384 MiB |
   *
   * ⚠️ **4096 is a desktop-only asset, and not because of the VRAM.** WebGL2
   * only guarantees `MAX_CUBE_MAP_TEXTURE_SIZE` of **2048** — a conformant
   * device may refuse a 4096 cube outright. Real hardware is usually far above
   * the floor (an M5 Max reports 16384) but a headset or a phone is exactly
   * where you would find the floor, and a sky that fails to load is a black
   * sky. Bake 4096 when you know the target; ship 2048 when you do not.
   */
  size?: number
  /** Far plane for the capture. Must reach past whatever you are photographing. */
  maxZ?: number
  /**
   * Things that billboard and must be aimed at the camera POSITION before the
   * capture — anything with a `facePoint(v)` method, which `b3d-galaxy` has.
   *
   * Defaults to every `<tosi-b3d-galaxy>` in the document, because forgetting
   * this is not a small mistake: leave a subject billboarding and it re-orients
   * to each face's view plane, so stars come out radially smeared and the faces
   * disagree at their seams.
   */
  subjects?: Array<{ facePoint(t: BABYLON.Vector3 | null): void }>
  /**
   * Roll the capture basis about its Z axis, in DEGREES — where the galactic
   * band sits in the finished sky.
   *
   * Baked in rather than applied later because the whole value of an
   * interactive baker is SEEING the framing while you choose it. (The cube can
   * still be re-oriented afterwards with `b3d-skybox`'s `starfieldTilt`; this is
   * for deciding, that is for adjusting.)
   */
  roll?: number
}

/**
 * THE RECIPE FOR `static/sky` — the pair every demo loads as
 * `/sky/nebula` + `/sky/stars`.
 *
 * Written down because it was not: reproducing the shipped sky meant reading
 * commit messages (100k stars in one, 42% out in another) and then proving the
 * guess by rebaking and byte-comparing. Bake with these and the data faces
 * are reproducible exactly.
 *
 * `tilt` is NOT baked in — the cube is photographed level and tilted where it
 * is used (`b3dSkybox({ starfieldTilt: SHIPPED_SKY.tilt })`), for the reason
 * on {@link defaultBakePose}. It is recorded here so the pair and the angle it
 * was framed for travel together.
 */
export const SHIPPED_SKY = {
  seed: 1234,
  stars: 100000,
  radius: 100,
  particleSize: 0.7,
  outFraction: 0.42,
  offPlane: 1,
  roll: 0,
  smoothSize: 256,
  dataSize: 1024,
  tilt: '12,25,58',
  /**
   * The VOXEL galaxy's dials (GALAXY-DESIGN.md) — used by `bin/bake-stars.ts`.
   * These are the values `static/sky` was baked with (approved by eye,
   * 2026-09-25): `bun bin/bake-stars.ts` then `bun bin/bake-nebula.ts`, with
   * no flags, reproduce both halves. Re-bake after changing them. The bright budget is well past GALAXY-DESIGN's ~5k
   * guess — at 5k the band loses its grain; 100k keeps it.
   */
  voxel: {
    brightBudget: 100000,
    dimBudget: 1000000,
    /** Generator units (disc radius 0.9) at which a dim star is at full brightness. */
    dimReach: 0.04,
    /** Faintest encoded dim star — also sets the gather radius. */
    floor: 0.02,
  },
} as const

/**
 * Where to stand, given a galaxy's radius.
 *
 * ⚠️ THIS IS IN THE GALAXY'S OWN FRAME. The first bake rotated the galaxy to
 * tilt the band and then used this position unchanged, so the camera stayed
 * where it was while the disc swung away from it — the sky came out
 * photographed from well outside, looking back at the galaxy as an object.
 * Tonio: "It should be baked from INSIDE the galaxy just off the plane, vs. way
 * off to the side."
 *
 * So: either leave the subject unrotated and use this directly (what the baker
 * does), or transform it by the subject's world matrix. **Do not rotate the
 * galaxy to get a tilt** — tilt the cube where it is USED, which is free, and
 * keeps the one number that must be right about placement out of the business
 * of orientation entirely.
 */
export function defaultBakePose(
  galaxyRadius: number,
  outFraction = 0.55,
  offPlane = 1
): { x: number; y: number; z: number } {
  return { x: galaxyRadius * outFraction, y: offPlane, z: 0 }
}

/**
 * Bundle baked faces into ONE zip for download.
 *
 * Twelve separate browser downloads are both rude and unreliable: Chrome
 * treats a burst of automatic downloads as a permission event and silently
 * drops the rest mid-burst (observed: four of six star faces arrived). One
 * file, no prompts, nothing dropped.
 *
 * STORED, not compressed — the PNGs are already compressed, so deflating
 * again buys nothing and costs time.
 */
export function facesToZip(
  groups: Array<{ prefix: string; faces: BakedFace[] }>,
  zipName = 'sky-faces.zip'
): { blob: Blob; name: string } {
  const files: Record<string, Uint8Array> = {}
  for (const { prefix, faces } of groups) {
    for (const f of faces) {
      const bin = atob(f.url.split(',')[1])
      const bytes = new Uint8Array(bin.length)
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
      files[`${prefix}_${f.name}.png`] = bytes
    }
  }
  const zipped = zipSync(files, { level: 0 })
  return {
    blob: new Blob([zipped], { type: 'application/zip' }),
    name: zipName,
  }
}

/**
 * Photograph the scene from one point as six cube faces.
 *
 * Returns PNG data URLs in Babylon's cube order, ready to save beside each
 * other as `<root>_px.png` … `<root>_nz.png` and loaded with
 * `new CubeTexture(root, scene)`.
 */
/**
 * Pull the point-like sky out of a `<tosi-b3d-galaxy>` as encodable objects.
 *
 * Stars AND the distant galaxies, because the latter are points too — measured:
 * baking the nebula system alone at 256 versus 1024 preserves energy and
 * bright-area almost exactly and loses only PEAK, and peak is what a point is.
 * The nebulae stay behind for the smooth cube, which is all they ever needed.
 *
 * The distant galaxies come through the galaxy's own accessor rather than a
 * size cut over the nebula system — at scale 3–9 they overlap the local
 * nebulae (2.25–7.5), so "small" would sweep in most of the nebulae along with
 * them. At the default 10k galaxy that was 1,400 extra discs in the cube; at
 * 100k it is fourteen thousand, crowding the cube and displacing real stars.
 * The data model knows which is which — ask it.
 *
 * Stars join the star DATA by particle index (the SPS builds one particle per
 * star, in order — the same alignment `hideStarAt` relies on). Two reasons,
 * both measured rather than assumed:
 *
 * - **Colour from the SPECTRAL CLASS, not the rgb.** A star's colour follows
 *   its class, so `palette: spectralPaletteIndex(spectralType)` is what the
 *   encoder stores — nearest-matching the particle's rgb made every star land
 *   in the near-white palette entries, which is exactly the "bright white
 *   dots, no colour" sky Tonio called out.
 * - **Brightness from the AUTHORED scale, not the clamped particle size.**
 *   `b3d-galaxy` clamps a star's apparent size (`maxStarApparentSize`), and at
 *   bake distance the clamp bit most of the population — the particle scales
 *   were nearly uniform, so the encoded magnitudes were too, and the display
 *   curve had no gradient to show. `StarData.scale` is derived from
 *   luminosity before any clamping, so the real magnitude range survives.
 */
export function starsFromGalaxy(
  galaxy: {
    getStarPoints?: () => SkyPoint[] | null
    getDistantGalaxyParticles?: () => SkyPoint[] | null
    getDistantStarParticles?: () => SkyPoint[] | null
    getGalaxyData?: () => {
      stars: Array<{ spectralType: string; scale: number }>
    } | null
  },
  eye: { x: number; y: number; z: number },
  options: { galaxyMaxScale?: number } = {}
): SkyObject[] {
  const out: SkyObject[] = []
  const stars = galaxy.getStarPoints?.() ?? []
  const starData = galaxy.getGalaxyData?.()?.stars ?? []
  let maxScale = 0
  for (const s of starData) maxScale = Math.max(maxScale, s.scale || 0)
  const norm = maxScale > 0 ? 1 / maxScale : 1
  /*
  THE DISC STARS ONLY. The star mesh also carries the distant stars on its
  tail, and they have their own loop below — walking the whole mesh encoded
  each of them TWICE, once here as a spectral-less 0.02 "star" and once
  properly. (Every sky baked before this fix carries the 3,000 duplicates;
  `placed` counted them.) Without star data there is no telling which is
  which, so everything is taken, as before.
  */
  const discCount =
    starData.length > 0 ? Math.min(stars.length, starData.length) : stars.length
  for (let i = 0; i < discCount; i++) {
    const p = stars[i]
    const data = starData[i]
    out.push({
      x: p.position.x - eye.x,
      y: p.position.y - eye.y,
      z: p.position.z - eye.z,
      // The star's OWN scale, pre-clamp — see the note above. A floor,
      // because a star encoded as zero is a star deleted.
      brightness: Math.max(0.02, Math.min(1, (data?.scale ?? 0) * norm)),
      r: p.color?.r ?? 1,
      g: p.color?.g ?? 1,
      b: p.color?.b ?? 1,
      // Colour follows the class; rgb is the fallback when the data is gone.
      spectral: data != null ? spectralValue(data.spectralType) : undefined,
    })
  }

  for (const o of distantShellObjects(
    galaxy.getDistantGalaxyParticles?.() ?? [],
    galaxy.getDistantStarParticles?.() ?? [],
    eye,
    options.galaxyMaxScale
  ))
    out.push(o)
  return out
}

/**
 * The shell OUTSIDE the disc — distant galaxies (small discs) and the dim
 * far-out stars — as sky objects. Shared by both star sources, so the rules
 * live once.
 */
export function distantShellObjects(
  galaxies: SkyPoint[],
  stars: SkyPoint[],
  eye: { x: number; y: number; z: number },
  galaxyMaxScale?: number
): SkyObject[] {
  const out: SkyObject[] = []
  /*
  The galaxies are the faintest things in the sky, and small discs rather than
  points — which is the whole reason the `size` field exists.
  */
  const cut = galaxyMaxScale ?? 9
  for (const p of galaxies) {
    const s = p.scaling?.x ?? 0
    if (s <= 0) continue
    out.push({
      x: p.position.x - eye.x,
      y: p.position.y - eye.y,
      z: p.position.z - eye.z,
      brightness: Math.max(0.05, Math.min(1, s / cut)),
      r: p.color?.r ?? 1,
      g: p.color?.g ?? 1,
      b: p.color?.b ?? 1,
      // A galaxy is a small DISC, which is the whole reason `size` exists.
      size: Math.min(1, s / cut),
    })
  }

  /*
  The dim far-out stars — isotropic texture in the empty regions. Brightness
  stays BELOW `BRIGHT_SPECTRAL_FLOOR` on purpose: they are the faintest
  things in the sky, and the encoder then stores them as the warm-yellow
  faint default rather than spending spectral precision on points the eye
  cannot read colour on anyway.
  */
  const dimCut = galaxyMaxScale != null ? galaxyMaxScale * 3.6 : 9
  for (const p of stars) {
    const s = p.scaling?.x ?? 0
    if (s <= 0) continue
    out.push({
      x: p.position.x - eye.x,
      y: p.position.y - eye.y,
      z: p.position.z - eye.z,
      // 0.05…0.25 — dim, but present.
      brightness: Math.max(0.05, Math.min(0.25, s / dimCut)),
      r: p.color?.r ?? 1,
      g: p.color?.g ?? 1,
      b: p.color?.b ?? 1,
    })
  }
  return out
}

/** Options for {@link starsFromVoxelGalaxy}. */
export interface VoxelSkyOptions {
  /**
   * The galaxy's radius in scene units — what `b3dGalaxy({ radius })` uses.
   * The generator's disc (radius 0.9) is scaled by `radius / 0.9` and turned
   * y-up, exactly as the live galaxy does, so a bake point means the same
   * place in both.
   */
  radius?: number
  /**
   * The distance, in GENERATOR units (disc radius 0.9), at which a dim star
   * shows its full intrinsic brightness. Nearer is clamped; farther falls off
   * with the square of distance.
   */
  dimReach?: number
  /**
   * The faintest a dim star may be and still be encoded. It also SETS the
   * gather radius: the distance at which the brightest dim star falls to it.
   */
  floor?: number
}

/**
 * The point sky from a {@link VoxelGalaxy}: every BRIGHT star, the DIM stars
 * near the eye, and the distant shell. (GALAXY-DESIGN.md, build step 2.)
 *
 * **Bright stars keep the rule the shipped sky was tuned with** — brightness
 * from the star's own scale, not its distance — so the look carries over.
 * **Dim stars fall off with distance**: a K or M dwarf is only visible nearby,
 * which is the whole point of generating them locally. The gather radius is
 * not a guess: it is where the BRIGHTEST dim star falls to `floor`, so nothing
 * that could clear the floor is left ungenerated.
 *
 * Brightness is normalised to the hottest star either mix CAN produce, not the
 * hottest one this seed happened to draw, so it cannot drift between galaxies.
 */
export function starsFromVoxelGalaxy(
  galaxy: VoxelGalaxy,
  /** The distant shell; omitted = the galaxy's own, `null` = none. */
  shell:
    | {
        distantGalaxies: NebulaData[]
        distantStars: DistantStarData[]
      }
    | null
    | undefined,
  eye: { x: number; y: number; z: number },
  options: VoxelSkyOptions = {}
): { objects: SkyObject[]; bright: number; dim: number; dimRadius: number } {
  const radius = options.radius ?? 100
  const reach = options.dimReach ?? 0.03
  const floor = options.floor ?? 0.02
  const sf = radius / 0.9
  // generator frame (z-up, unit-ish) ↔ scene frame (y-up, scaled)
  const toScene = (p: { x: number; y: number; z: number }) => ({
    x: p.x * sf,
    y: p.z * sf,
    z: p.y * sf,
  })
  const eyeGen = { x: eye.x / sf, y: eye.z / sf, z: eye.y / sf }

  const hottest = (mix: MixEntry[]) =>
    Math.max(
      ...mix.map(
        (m) =>
          starDetailFor(new CheapPRNG(0), 0, m.spectralClass, m.minIndex).scale
      )
    )
  const norm = 1 / hottest(galaxy.options.brightMix)
  const dimMax = Math.min(1, hottest(galaxy.options.dimMix) * norm)
  const dimRadius = reach * Math.sqrt(dimMax / floor)

  const out: SkyObject[] = []
  const push = (s: VoxelStar, brightness: number) => {
    const p = toScene(s.position)
    out.push({
      x: p.x - eye.x,
      y: p.y - eye.y,
      z: p.z - eye.z,
      brightness,
      r: s.rgb[0] / 255,
      g: s.rgb[1] / 255,
      b: s.rgb[2] / 255,
      spectral: spectralValue(s.spectralType),
    })
  }

  const bright = galaxy.brightStars()
  for (const s of bright) {
    push(s, Math.max(floor, Math.min(1, s.scale * norm)))
  }

  let dim = 0
  for (const s of galaxy.dimStarsNear(eyeGen, dimRadius)) {
    const d = Math.hypot(
      s.position.x - eyeGen.x,
      s.position.y - eyeGen.y,
      s.position.z - eyeGen.z
    )
    const falloff = d <= reach ? 1 : (reach / d) ** 2
    const b = Math.min(1, s.scale * norm) * falloff
    if (b < floor) continue
    push(s, b)
    dim++
  }

  if (shell === undefined) shell = galaxy.shell()
  if (shell != null) {
    const point = (
      p: { x: number; y: number; z: number },
      scale: number,
      rgb: [number, number, number]
    ): SkyPoint => ({
      position: toScene(p),
      scaling: { x: scale },
      color: { r: rgb[0] / 255, g: rgb[1] / 255, b: rgb[2] / 255 },
    })
    for (const o of distantShellObjects(
      shell.distantGalaxies.map((g) => point(g.position, g.scale, g.rgb)),
      shell.distantStars.map((d) => point(d.position, d.scale, d.rgb)),
      eye
    ))
      out.push(o)
  }
  return { objects: out, bright: bright.length, dim, dimRadius }
}

/**
 * Encoded faces → PNG data URLs, ready to save beside a skybox.
 *
 * ⚠️ **PNG, and it must stay PNG — but that is only half the rule.** These are
 * packed fields, not pictures, and a lossy codec would quantise a sub-texel
 * position into a different position and a brightness into a different star.
 * JPEG here would not look slightly worse, it would move the sky.
 *
 * The other half is that the PNG must not pass through a CANVAS. The canvas 2D
 * backing store is premultiplied, and a star's alpha byte is a palette index
 * (0..9) rather than an opacity — so `putImageData` rounds the RGB of every
 * low-alpha texel toward zero, `toDataURL` faithfully saves the destruction,
 * and the shader decodes a sky with its faint stars silently deleted.
 * Measured against the real 100k-star galaxy before this was written: 67% of
 * one face's occupied texels corrupted or gone, positions snapped to 0/255,
 * before any compression question arose. `pngEncode` writes the bytes
 * directly; a texel with alpha 0 and nonzero RGB survives exactly.
 */
export async function facesToPngs(
  faces: Uint8Array[],
  size: number
): Promise<BakedFace[]> {
  const out: BakedFace[] = []
  for (let i = 0; i < faces.length; i++) {
    const bytes = pngEncode(faces[i], size, size)
    // btoa over 1 MB in one slice is fine; the chunking is for safety, not size.
    let binary = ''
    const CHUNK = 0x8000
    for (let at = 0; at < bytes.length; at += CHUNK) {
      binary += String.fromCharCode(...bytes.subarray(at, at + CHUNK))
    }
    out.push({
      name: FACE_NAMES[i],
      url: `data:image/png;base64,${btoa(binary)}`,
    })
  }
  return out
}

/**
 * Bake a galaxy into the PAIR of cubes the sky wants: a small smooth one for
 * the nebulae and a data one for the points.
 *
 * The two are separate because they are different KINDS of thing, measured
 * rather than assumed — see `starfield-codec`. The smooth half is
 * low-frequency and survives 256; the points need position, not pixels.
 */
export async function bakeSkyPair(
  scene: BABYLON.Scene,
  galaxy: Parameters<typeof starsFromGalaxy>[0] & {
    getStarMesh?: () => BABYLON.AbstractMesh | null
  },
  options: SkyboxBakeOptions & {
    dataSize?: number
    smoothSize?: number
    /**
     * The point sky, precomputed — e.g. from {@link starsFromVoxelGalaxy}.
     * Omitted, it is read from the live galaxy's particles as before.
     */
    objects?: SkyObject[]
  }
): Promise<{
  smooth: BakedFace[]
  data: BakedFace[]
  placed: number
  lost: number
}> {
  /*
  HIDE THE STARS FOR THE SMOOTH BAKE. They are about to be stored exactly;
  rendering them into the nebula cube as well would double every one of them,
  and the smeared copy is the thing this whole exercise exists to delete.
  */
  const mesh = galaxy.getStarMesh?.() ?? null
  const wasVisible = mesh?.isVisible ?? false
  if (mesh != null) mesh.isVisible = false
  let smooth: BakedFace[]
  try {
    smooth = await bakeSkyboxCube(scene, {
      ...options,
      size: options.smoothSize ?? 256,
    })
  } finally {
    if (mesh != null) mesh.isVisible = wasVisible
  }

  const objects =
    options.objects ??
    starsFromGalaxy(galaxy, {
      x: options.x,
      y: options.y,
      z: options.z,
    })
  const size = options.dataSize ?? 1024
  const enc = encodeStarfield(objects, size)
  return {
    smooth,
    data: await facesToPngs(enc.faces, size),
    placed: enc.placed,
    lost: enc.collided,
  }
}

export async function bakeSkyboxCube(
  scene: BABYLON.Scene,
  options: SkyboxBakeOptions
): Promise<BakedFace[]> {
  const size = options.size ?? 512
  const engine = scene.getEngine()

  /*
  SAY SO IF THIS MACHINE COULD NOT DISPLAY WHAT IT IS ABOUT TO BAKE.

  The bake itself is a 2D render target, so it will happily produce faces larger
  than this device can ever load back as a CUBE — and the failure then happens
  somewhere else entirely, at load time, as a black sky. Cheap to check here
  where the number is in hand, and the warning names the limit rather than the
  symptom.

  It is only a warning: baking a 4096 asset on a modest machine for a beefier
  one is a legitimate thing to be doing, and refusing would stop it.
  */
  const maxCube = engine.getCaps().maxCubemapTextureSize
  if (maxCube > 0 && size > maxCube) {
    console.warn(
      `skybox-baker: baking ${size}px faces, but this device caps cube maps at ` +
        `${maxCube}px — it will not be able to load the result as a skybox. ` +
        `WebGL2 only guarantees 2048.`
    )
  }

  /*
  SIX CAMERAS AFTER ALL — `ReflectionProbe` read back BLACK.

  The probe is the better idea on paper and it is what this used to do: it
  renders a correctly-oriented cube, so the face conventions stay Babylon's
  problem. But `cubeTexture.readPixels(face)` returned all zeros here (verified:
  max channel 0 across every face), and a bake that silently produces six black
  squares is worse than a bake that is slightly more code.

  So the faces are rendered through `CreateScreenshotUsingRenderTargetAsync`,
  which is a path Babylon exercises constantly, and the orientation table below
  is written out rather than trusted to memory. The ±Y rows are the ones that
  bite: they need a different UP, because "up" is undefined when you are looking
  straight up.
  */
  const V = BABYLON.Vector3
  const FACES: Array<{
    name: string
    dir: BABYLON.Vector3
    up: BABYLON.Vector3
  }> = [
    { name: 'px', dir: new V(1, 0, 0), up: new V(0, 1, 0) },
    { name: 'nx', dir: new V(-1, 0, 0), up: new V(0, 1, 0) },
    { name: 'py', dir: new V(0, 1, 0), up: new V(0, 0, -1) },
    { name: 'ny', dir: new V(0, -1, 0), up: new V(0, 0, 1) },
    { name: 'pz', dir: new V(0, 0, 1), up: new V(0, 1, 0) },
    { name: 'nz', dir: new V(0, 0, -1), up: new V(0, 1, 0) },
  ]

  /*
  ROLL THE BASIS, NOT THE SUBJECT. Rotating the galaxy to tilt the band moves it
  out from under the camera — that bug is recorded on `defaultBakePose`. Rolling
  the six face directions turns the sky instead, which is what "rotate the
  skybox" should mean and cannot displace the observer.
  */
  const roll = ((options.roll ?? 0) * Math.PI) / 180
  if (roll !== 0) {
    const R = BABYLON.Matrix.RotationZ(roll)
    for (const f of FACES) {
      BABYLON.Vector3.TransformNormalToRef(f.dir, R, f.dir)
      BABYLON.Vector3.TransformNormalToRef(f.up, R, f.up)
    }
  }

  const at = new V(options.x, options.y, options.z)
  const cam = new BABYLON.FreeCamera(
    'skybox-bake-cam',
    at.clone(),
    scene,
    false
  )
  // 90° EXACTLY, and square — a cube face is a quarter turn per axis. Anything
  // else leaves gaps at the seams that no amount of tuning will close.
  cam.fov = Math.PI / 2
  cam.fovMode = BABYLON.Camera.FOVMODE_VERTICAL_FIXED
  cam.minZ = 0.1
  cam.maxZ = options.maxZ ?? 5000

  /*
  PIN THE VIEWPOINT — inside the baker, so it cannot be skipped.

  `b3d-galaxy` billboards in its vertex shader toward the rendering camera's
  POSITION, which the six faces share, so the seams agree on their own.
  `facePoint` still matters: it fixes that viewpoint for the whole capture and
  switches on the apparent-size clamp, which only means something from one
  known point.

  (It once existed and NOTHING CALLED IT, back when billboarding was per-face
  on the CPU — Tonio, from the result: "I think you're pointing the stars at
  the galactic origin." A guard that lives in the documentation is not a guard.
  It belongs here.)
  */
  const subjects =
    options.subjects ??
    (Array.from(
      document.querySelectorAll('tosi-b3d-galaxy')
    ) as unknown as Array<{ facePoint(t: BABYLON.Vector3 | null): void }>)
  for (const s of subjects) s.facePoint?.(at)

  const previous = scene.activeCamera
  const out: BakedFace[] = []
  try {
    for (const face of FACES) {
      cam.position.copyFrom(at)
      cam.upVector.copyFrom(face.up)
      cam.setTarget(at.add(face.dir))
      scene.activeCamera = cam
      /*
      RENDER NORMALLY FIRST, so anything camera-dependent settles.

      The screenshot's own render target does not run the scene's beforeRender
      observers, so anything that updates from one — CPU billboards, as a
      `SolidParticleSystem` does — would otherwise be photographed still facing
      the PREVIOUS face (the galaxy did exactly that, as radial streaking,
      before it moved its billboarding into the vertex shader). One throwaway
      frame per face is cheap, and keeps the baker honest for subjects that
      are not the galaxy.
      */
      scene.render()
      const url = await BABYLON.Tools.CreateScreenshotUsingRenderTargetAsync(
        engine,
        cam,
        { width: size, height: size },
        'image/png',
        1,
        false
      )
      out.push({ name: face.name, url })
    }
  } finally {
    // Hand orientation back to the live camera, or the scene stays frozen
    // facing a point the viewer has since left.
    for (const s of subjects) s.facePoint?.(null)
    scene.activeCamera = previous
    cam.dispose()
  }
  return out
}
